<template>
    <!-- ScrollArea: Chats -->
    <div class="team-chat-list-wrapper">
        <b-skeleton-wrapper v-if="isFetchingChats">
            <template v-for="index in 10">
                <div class="d-flex justify-content-start p-2">
                    <b-skeleton class="mr-50 round-large" size="34px" type="avatar"></b-skeleton>
                    <div class="flex-grow-1 pl-75">
                        <b-skeleton class="mb-50" width="80%"></b-skeleton>
                        <b-skeleton class="mb-25" width="100%"></b-skeleton>
                    </div>
                </div>
                <hr class="hr-skeleton w-100 p-0 m-0" />
            </template>
        </b-skeleton-wrapper>

        <div class="team-chat-scroll-area-wrapper list-group scroll-area scroll-container auto">
            <template v-if="!isFetchingChats">
                <div v-if="!hasChats" class="no-chats pt-2 text-center">
                    <img :alt="$t('inbox.no-chats')" src="~@/assets/images/team-chats-zero-state.svg" />
                    <div class="mt-2 mb-3">{{ $t("inbox.no-chats") }}</div>

                    <b-button variant="primary" @click="toggleCreateTeamChat">
                        {{ $t("team-chats.create-chat") }}
                    </b-button>
                </div>
                <ul v-else class="chat-users-list chat-list media-list">
                    <team-chat
                        v-for="chat in chats"
                        :key="chat.id"
                        :chat="chat"
                        :class="{ active: activeChat && activeChat.id === chat.id }"
                        :user="user"
                        class="round-extra-large"
                        @click="onChatClick(chat.id)"
                        @contextmenu.prevent="$refs.contextMenu.open($event, { chat })"
                    />
                </ul>
            </template>
        </div>

        <vue-context ref="contextMenu" v-slot="{ data }">
            <li v-for="menuItem in filterContextMenuData(data)" :key="menuItem.text">
                <b-link
                    class="d-flex align-items-center"
                    @click.prevent="onContextMenuItemClick(menuItem.eventName, data)"
                >
                    <font-awesome-icon v-if="menuItem.icon" :icon="menuItem.icon" />
                    <span class="ml-75">{{ menuItem.text }}</span>
                </b-link>
            </li>
        </vue-context>
    </div>
</template>

<script>
import TeamChat from "./teamChatItem.vue";
import { BButton, BImg, BLink, BOverlay, BSkeleton, BSkeletonWrapper } from "bootstrap-vue";
import { computed } from "@vue/composition-api";
import VueContext from "vue-context";
import useActiveTeamChat from "../useActiveTeamChat";
import useUnreadChats from "@/composables/v2.1/useUnreadChats";
import { InboxModuleType } from "@/types/inboxModule.type";

export default {
    components: {
        BButton,
        BOverlay,
        BImg,
        TeamChat,
        BLink,
        VueContext,
        BSkeletonWrapper,
        BSkeleton,
    },
    props: {
        user: {
            type: Object,
            required: true,
        },
        chats: {
            type: Array,
            required: true,
        },
        isFetchingChats: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            contextMenuData: [
                {
                    icon: "envelope-open",
                    text: this.$t("team-chats.contextual-menu.mark-as-read"),
                    eventName: "set-read",
                },
                {
                    icon: "envelope",
                    text: this.$t("team-chats.contextual-menu.mark-as-unread"),
                    eventName: "set-unread",
                },
                {
                    icon: "fa-arrow-right-from-bracket",
                    text: this.$t("team-chats.contextual-menu.leave-chat"),
                    eventName: "leave-chat",
                },
                {
                    icon: "trash",
                    text: this.$t("team-chats.contextual-menu.delete-chat"),
                    eventName: "delete-chat",
                },
            ],
        };
    },
    emits: ["toggle-create-team-chat", "team-chat-clicked", "set-read", "leave-chat", "delete-chat"],
    methods: {
        filterContextMenuData(data) {
            if (!data) {
                return;
            }

            let forDeletion = [];
            if (data.chat.isArchived) {
                forDeletion = forDeletion.concat(["leave-chat", "delete-chat"]);
            } else {
                forDeletion.push(data.chat.userId === this.user.id ? "leave-chat" : "delete-chat");
            }
            forDeletion.push(
                data.chat.participants.find((participant) => participant.userId === this.user.id).chatStatus ===
                    "unread" || data.chat.unreadMessages
                    ? "set-unread"
                    : "set-read"
            );
            return this.contextMenuData.filter((menuData) => !forDeletion.includes(menuData.eventName));
        },
        toggleCreateTeamChat() {
            this.$emit("toggle-create-team-chat");
        },
        onChatClick(id) {
            this.$emit("team-chat-clicked", { id });
        },
        onContextMenuItemClick(eventName, data) {
            // @TEMP because of time, chosen the easy way with switch-case.
            switch (eventName) {
                case "set-read":
                    this.setRead(data.chat);
                    return this.$emit("set-status", {
                        chatStatus: "read",
                        chatId: data.chat.id,
                        userId: this.user.id,
                        participantId: data.chat.participants.find((participant) => participant.userId === this.user.id)
                            ?.id,
                    });
                case "set-unread":
                    this.setUnRead(data.chat);
                    return this.$emit("set-status", {
                        chatStatus: "unread",
                        chatId: data.chat.id,
                        userId: this.user.id,
                        participantId: data.chat.participants.find((participant) => participant.userId === this.user.id)
                            ?.id,
                    });
                case "leave-chat":
                    return this.$emit("leave-chat", {
                        chatId: data.chat.id,
                        userId: this.user.id,
                        participantId: data.chat.participants.find((participant) => participant.userId === this.user.id)
                            ?.id,
                    });
                case "delete-chat":
                    return this.$emit("delete-chat", {
                        chatId: data.chat.id,
                        userId: this.user.id,
                        participantId: data.chat.participants.find((participant) => participant.userId === this.user.id)
                            ?.id,
                    });
            }
        },
        setRead(chat) {
            const { updateUnreadChat } = useUnreadChats(InboxModuleType.TEAM);
            updateUnreadChat({
                ...chat,
                chatId: chat.id,
            });
        },
        setUnRead(chat) {
            const { addUnreadChat } = useUnreadChats(InboxModuleType.TEAM);
            addUnreadChat({
                ...chat,
                chatId: chat.id,
            });
        },
    },
    setup(props) {
        const { activeChat } = useActiveTeamChat();
        const hasChats = computed(() => Boolean(props.chats?.length));

        return {
            hasChats,
            activeChat,
        };
    },
};
</script>

<style lang="scss">
@import "~@core/scss/vue/libs/vue-context.scss";
@import "~@/assets/scss/components/inbox/chatLeftSidebar.scss";
</style>
